import React, { useState } from 'react';
import { FaGreaterThan, FaLessThan } from 'react-icons/all';
import Flatpickr from 'react-flatpickr';
import { CalendarInputComponentProps } from './CalendarInputComponent.types';
import dayjs from 'dayjs';
import './CalendarInputComponent.scss';
import clsx from 'clsx';
import { ButtonComponent } from '../Button/ButtonComponent';

const CalendarInputComponent = React.memo(
  ({
    date,
    className,
    setDate,
    dateFormat = 'YYYY-MM-DD',
    dateFormatToShow = 'dddd, DD MMMM',
    inputWrapperClassName,
    variant = 'outlined',
    blockPastDates = false,
    blockFutureDates = false,
    maxDate,
    ...props
  }: CalendarInputComponentProps) => {
    const [activeDate, setActiveDate] = useState(dayjs(date).toDate());

    const datePickerOptions = {
      minDate: blockPastDates ? 'today' : null,
      maxDate: maxDate || (blockFutureDates ? 'today' : null),
      disableMobile: true,
      dateFormat,
      showMonths: 1,
      shorthandCurrentMonth: true,
      locale: { firstDayOfWeek: 1 },
    };

    const isToday = dayjs(date).isSame(dayjs(), 'day');
    const previousDateBlocked = (): boolean => blockPastDates && isToday;
    const nextDayBlocked = (): boolean => {
      if (maxDate != null) {
        return dayjs(date).isSame(dayjs(maxDate), 'day');
      } else if (blockFutureDates) {
        return isToday;
      }
      return false;
    };

    const onDatePickerChanged = (selectedDates: Date[]) => {
      setDate(dayjs(selectedDates[0]).format(dateFormat));
    };

    const onPreviousDateButtonClicked = () => {
      if (!previousDateBlocked()) {
        const previousDate = dayjs(date).subtract(1, 'day');
        setDate(previousDate.format(dateFormat));
        setActiveDate(previousDate.toDate());
      }
    };

    const onNextDateButtonClicked = () => {
      if (!nextDayBlocked()) {
        const nextDate = dayjs(date).add(1, 'day');
        setDate(nextDate.format(dateFormat));
        setActiveDate(nextDate.toDate());
      }
    };

    return (
      <div className={className}>
        <Flatpickr
          onChange={onDatePickerChanged}
          options={datePickerOptions}
          value={activeDate}
          render={(props, ref) => (
            <div
              className={clsx('calendar-button-wrapper', inputWrapperClassName)}
            >
              <div
                className={clsx('backward-button', {
                  blocked: previousDateBlocked(),
                })}
                onClick={onPreviousDateButtonClicked}
              >
                <FaLessThan />
              </div>
              <div ref={ref}>
                <ButtonComponent
                  variant={variant}
                  className={clsx('form-control', 'calendar-button')}
                  id="calendar-button"
                  name="calendar-button"
                  text={
                    isToday
                      ? `Today, ${dayjs(date).format('DD MMMM')}`
                      : dayjs(date).format(dateFormatToShow)
                  }
                />
              </div>
              <div
                className={clsx('forward-button', {
                  blocked: nextDayBlocked(),
                })}
                onClick={onNextDateButtonClicked}
              >
                <FaGreaterThan />
              </div>
            </div>
          )}
        />
      </div>
    );
  }
);

CalendarInputComponent.displayName = 'CalendarInputComponent';
/* This is changed to False because it's showing the long debugger in console.log even for re-rendering one time */
/* In the PlannerMap, this component only re-rendering whenever the props has changed */
/* but it keeps showing a long debugger in console.log, so i'm changing this value to False */
/* You can enable this back whenever you want to debugging something */
CalendarInputComponent.whyDidYouRender = false;

export { CalendarInputComponent };
